import React, { useEffect, useReducer, useState, useCallback } from 'react';
import List from '@mui/icons-material/List';
import PropTypes from 'prop-types';
import { buildExportedRightsDownloadUrl } from '../../../../api';
import { USER_GROUPS_QUERY } from 'features/common/components/userGroup/userGroupList/queries';
import { DOCUMENT_TYPE_WITH_ALLOWED_STATES_QUERY } from './queries';
import {
  SAVE_DOCUMENT_TYPE_WITH_ALLOWED_STATES,
  EXPORT_DOCUMENT_ALLOWED_STATE_RIGHTS_LIST,
  IMPORT_DOCUMENT_ALLOWED_STATE_RIGHTS
} from './mutations';
import { DOCUMENT_TYPE_WITH_ALLOWED_STATES_REMOVED_OR_UPDATED, IMPORT_DOCUMENT_ALLOWED_STATE_IMPORTED } from './subscriptions';
import AllowedStateListCard from './AllowedStateListCard';
import AllowedStateListFiltersComponent from './AllowedStateListFiltersComponent';
import { Card } from '@totalsoft/rocket-ui';
import { Pagination } from '@totalsoft/rocket-ui';
import { FakeText } from '@totalsoft/rocket-ui';
import { useDebounce } from 'utils/customHooks';
import { reducer, initialState } from '../../../../reducers/allowedStates/containerReducer';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useError } from '@ts-private/apollo-tools';
import { useToast } from 'hooks/toasts/useToast';
import { convertBase64ToFile } from 'utils/base64FileConversion';
import saveAs from 'file-saver';
import { getOidcConfigName } from 'utils/functions';
import { useOidcAccessToken } from '@axa-fr/react-oidc';

const AllowedStateListContainer = props => {
  const { documentTypeId, onChangedDocumentTypeName, onSaveFnChanged, onExportFnChanged, onImportFnChanged, onEdit } = props;
  const [localState, dispatch] = useReducer(reducer, initialState);
  const [listFilter, setListFilter] = useState({ stateFilter: '', userGroupFilter: '' });
  const debouncedListFilter = useDebounce(listFilter, 150);
  const showError = useError();
  const addToast = useToast();
  const { t } = useTranslation();
  const client = useApolloClient();
  const { accessToken } = useOidcAccessToken(getOidcConfigName());

  const onListFilterChanged = (prop, value) => setListFilter({ ...listFilter, [prop]: value });

  const { data: documentTypeWithAllowedStatesData, loading } = useQuery(DOCUMENT_TYPE_WITH_ALLOWED_STATES_QUERY, {
    onCompleted: data => {
      dispatch({
        type: 'OnInitDocumentTypeWithAllowedStates',
        documentTypeWithAllowedStates: {
          ...data.documentTypeWithAllowedStates,
          allowedStates: data?.documentTypeWithAllowedStates?.allowedStates.filter(x =>
            x.documentStateName?.toLowerCase().includes(listFilter.stateFilter.toLowerCase())
          )
        }
      });
    },
    variables: {
      id: documentTypeId
    },
    notifyOnNetworkStatusChange: true
  });

  const { data: userGroupsData } = useQuery(USER_GROUPS_QUERY, {
    onCompleted: data => {
      dispatch({
        type: 'OnInitUserGroups',
        userGroups: data?.userGroupsConfig.filter(x => x.name.toLowerCase().includes(listFilter.userGroupFilter.toLowerCase()))
      });
    }
  });

  const [exportDocumentAllowedStateRights] = useMutation(EXPORT_DOCUMENT_ALLOWED_STATE_RIGHTS_LIST, {
    onCompleted: async ({ exportDocumentAllowedStateRights }) => {
      let downloadLink = null;
      try {
        if (exportDocumentAllowedStateRights.fileBytes) {
          saveAs(convertBase64ToFile(exportDocumentAllowedStateRights.fileBytes), exportDocumentAllowedStateRights.fileName);
          return;
        }
        downloadLink = await buildExportedRightsDownloadUrl(
          documentTypeId,
          exportDocumentAllowedStateRights.filePath,
          exportDocumentAllowedStateRights.fileName,
          accessToken
        );
      } catch (error) {
        showError(error);
      }
      if (downloadLink) {
        window.location.href = downloadLink;
      }
    }
  });

  const [importDocumentAllowedStateRights] = useMutation(IMPORT_DOCUMENT_ALLOWED_STATE_RIGHTS);

  const [saveDocumentTypeWithAllowedStates] = useMutation(SAVE_DOCUMENT_TYPE_WITH_ALLOWED_STATES, {
    onError: () => {
      dispatch({ type: 'OnSaveFailed' });
    }
  });

  useSubscription(DOCUMENT_TYPE_WITH_ALLOWED_STATES_REMOVED_OR_UPDATED, {
    onData: () => {
      addToast(t('DocumentTypes.DocumentWithAllowedStates.SavingSucceeded'), 'success');
      client.cache.evict({ id: `DocumentTypeWithAllowedStates:${documentTypeId}` });
      client.cache.gc();
    }
  });

  useSubscription(IMPORT_DOCUMENT_ALLOWED_STATE_IMPORTED, {
    onData: _ => {
      addToast(t('DocumentTypes.DocumentWithAllowedStates.DocumentAllowedStateRightsImported'), 'success');
      client.cache.evict({ id: `DocumentTypeWithAllowedStates:${documentTypeId}` });
      client.cache.gc();
    }
  });

  const onPageChange = useCallback(page => {
    dispatch({ type: 'OnPageChange', page: page });
  }, []);

  const onRowsPerPageChange = useCallback(pageSize => {
    dispatch({ type: 'OnRowsPerPageChange', pageSize: pageSize });
  }, []);

  const onSave = useCallback(() => {
    dispatch({ type: 'OnSaving' });
    const changedAllowedStatesPermissions = Object.keys(localState.changedAllowedStatesPermissions).map(allowedStateId => {
      return {
        documentTypeAllowedStateId: parseInt(allowedStateId),
        userGroupPermissions: Object.keys(localState.changedAllowedStatesPermissions[allowedStateId]).map(userGroupId => {
          return {
            userGroupId: parseInt(userGroupId),
            isAllowed: localState.changedAllowedStatesPermissions[allowedStateId][userGroupId]
          };
        })
      };
    });
    saveDocumentTypeWithAllowedStates({
      variables: {
        documentTypeId: localState.documentTypeId,
        allowedStatesPermissions: changedAllowedStatesPermissions,
        deletedAllowedStates: localState.deletedAllowedStates
      }
    });
  }, [localState, saveDocumentTypeWithAllowedStates]);

  const onRemove = allowedStateId => () => {
    dispatch({ type: 'OnRemoveAllowedState', allowedStateId });
  };

  const onExport = useCallback(
    async fileType => {
      exportDocumentAllowedStateRights({ variables: { documentTypeId: documentTypeId, fileType: fileType } });
    },
    [exportDocumentAllowedStateRights, documentTypeId]
  );

  const onImport = useCallback(
    selectedFile => {
      if (!selectedFile) return;
      importDocumentAllowedStateRights({ variables: { documentTypeId: documentTypeId, file: selectedFile } });
    },
    [documentTypeId, importDocumentAllowedStateRights]
  );

  useEffect(() => {
    if (userGroupsData && userGroupsData.userGroupsConfig)
      dispatch({
        type: 'OnFilterList',
        documentTypeWithAllowedStates: {
          ...documentTypeWithAllowedStatesData?.documentTypeWithAllowedStates,
          allowedStates: documentTypeWithAllowedStatesData?.documentTypeWithAllowedStates?.allowedStates.filter(x =>
            x.documentStateName?.toLowerCase().includes(listFilter.stateFilter.toLowerCase())
          )
        },
        userGroups: userGroupsData?.userGroupsConfig.filter(x => x.name.toLowerCase().includes(listFilter.userGroupFilter.toLowerCase()))
      });
  }, [documentTypeWithAllowedStatesData, userGroupsData, listFilter, debouncedListFilter]);

  useEffect(() => {
    if (documentTypeWithAllowedStatesData?.documentTypeWithAllowedStates)
      onChangedDocumentTypeName(documentTypeWithAllowedStatesData?.documentTypeWithAllowedStates.name);
  }, [onChangedDocumentTypeName, documentTypeWithAllowedStatesData]);

  useEffect(() => {
    onSaveFnChanged(onSave, localState.allowSave);
  }, [onSaveFnChanged, onSave, localState.allowSave]);

  useEffect(() => {
    onImportFnChanged(onImport);
  }, [onImportFnChanged, onImport]);

  useEffect(() => {
    onExportFnChanged(onExport);
  }, [onExport, onExportFnChanged, localState.disabledExport]);

  return (
    <Card icon={List} iconColor='primary'>
      {loading ? (
        <FakeText lines={localState.pager.pageSize} />
      ) : userGroupsData && userGroupsData.userGroupsConfig ? (
        <>
          <AllowedStateListFiltersComponent listFilter={listFilter} onListFilterChanged={onListFilterChanged} />
          <AllowedStateListCard
            changed={localState.allowSave}
            localState={localState}
            onEdit={onEdit}
            onRemove={onRemove}
            dispatch={dispatch}
            onSave={onSave}
          />
          <Pagination
            loading={loading}
            count={localState.documentTypeWithAllowedStates.allowedStates?.length || 0}
            pageSize={localState.pager.pageSize}
            page={localState.pager.page}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
          />
        </>
      ) : (
        <></>
      )}
    </Card>
  );
};

AllowedStateListContainer.propTypes = {
  documentTypeId: PropTypes.number.isRequired,
  onChangedDocumentTypeName: PropTypes.func.isRequired,
  onSaveFnChanged: PropTypes.func.isRequired,
  onExportFnChanged: PropTypes.func.isRequired,
  onImportFnChanged: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired
};

export default AllowedStateListContainer;
